import { logger } from "../utils/logger";
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiUrl } from '../utils/apiConfig';

// ============================================
// INTERFACES
// ============================================

interface ProcessStepsData {
  id: string;
  
  viewTabs: {
    orbit: { icon: string; text: string };
    timeline: { icon: string; text: string };
  };
  
  centerSun: {
    icon: string;
    line1: string;
    line2: string;
    line3: string;
  };
  
  steps: {
    number: number;
    title: string;
    description: string;
    duration: string;
    deliverables: string[];
    durationIcon: string;
    deliverablesIcon: string;
    collaboration: {
      level: string;
      icon: string;
      description: string;
    };
    flexibility: string;
  }[];
  
  stepCardExtras?: {
    durationIcon: string;
    durationLabel: string;
    involvementIcon: string;
    involvementLabel: string;
    involvementEmoji: string;
    involvementLevel: string;
    involvementDescription: string;
    flexibilityIcon: string;
    flexibilityLabel: string;
    flexibilityText: string;
  }[];
  
  timelineView: {
    quickOverview: {
      icon: string;
      title: string;
      totalTime: string;
      gates: string;
    };
    durationIcon: string;
    involvementIcon: string;
    flexibilityIcon: string;
    collaborationLevels: {
      level: string;
      icon: string;
      description: string;
    }[];
    revisionPolicies: string[];
    totalSummary: {
      icon: string;
      title: string;
      features: string;
      deliveryRate: string;
      deliveryText: string;
    };
  };
}

// ============================================
// DEFAULT DATA (FALLBACK)
// ============================================

const DEFAULT_DATA: ProcessStepsData = {
  id: 'process-steps',
  
  viewTabs: {
    orbit: { icon: '🪐', text: 'Orbit View' },
    timeline: { icon: '📊', text: 'Timeline View' },
  },
  
  centerSun: {
    icon: 'Clapperboard',
    line1: 'Your',
    line2: 'Animation',
    line3: 'Project',
  },
  
  steps: [
    {
      number: 1,
      title: "Discovery & Strategy",
      description: "We start by understanding your goals, audience, and technical requirements. What problem are we solving? Who needs to understand it? What action should they take?",
      duration: "1-2 weeks",
      deliverables: ["Project brief", "Creative strategy", "Technical requirements", "Success metrics"],
      durationIcon: "Clock",
      deliverablesIcon: "CheckCircle2",
      collaboration: {
        level: "Heavy",
        icon: "🤝",
        description: "Strategy sessions with your team",
      },
      flexibility: "Unlimited strategy iterations",
    },
    {
      number: 2,
      title: "Script & Storyboard",
      description: "Our team develops the narrative and visual approach. You'll see exactly what the animation will communicate before production begins.",
      duration: "1-2 weeks",
      deliverables: ["Detailed script", "Visual storyboard", "Technical review", "Stakeholder approval"],
      durationIcon: "Clock",
      deliverablesIcon: "CheckCircle2",
      collaboration: {
        level: "High",
        icon: "🤝",
        description: "Your approval required",
      },
      flexibility: "Up to 2 revision rounds included",
    },
    {
      number: 3,
      title: "3D Modeling & Asset Creation",
      description: "We build accurate 3D models of your products, equipment, or processes. Every detail matters—we work closely with your technical team to ensure precision.",
      duration: "2-3 weeks",
      deliverables: ["3D models", "Textures & materials", "Environment setup", "Technical validation"],
      durationIcon: "Clock",
      deliverablesIcon: "CheckCircle2",
      collaboration: {
        level: "Medium",
        icon: "👁️",
        description: "Review for technical accuracy",
      },
      flexibility: "Technical validation review",
    },
    {
      number: 4,
      title: "Animation & Motion",
      description: "Your story comes to life. We animate movements, processes, and interactions with technical accuracy and visual clarity.",
      duration: "2-4 weeks",
      deliverables: ["Animated sequences", "Camera movements", "Timing refinement", "Preview renders"],
      durationIcon: "Clock",
      deliverablesIcon: "CheckCircle2",
      collaboration: {
        level: "Medium",
        icon: "👁️",
        description: "Preview checkpoints",
      },
      flexibility: "Preview renders at 25%, 50%, 75%",
    },
    {
      number: 5,
      title: "Review & Refinement",
      description: "You review the animation and provide feedback. We refine until it perfectly represents your process and meets your objectives.",
      duration: "1-2 weeks",
      deliverables: ["Review sessions", "Revisions", "Technical verification", "Final approval"],
      durationIcon: "Clock",
      deliverablesIcon: "CheckCircle2",
      collaboration: {
        level: "High",
        icon: "🤝",
        description: "Your feedback drives refinements",
      },
      flexibility: "Up to 3 comprehensive revision rounds",
    },
    {
      number: 6,
      title: "Final Production & Delivery",
      description: "We add voiceover, music, graphics, and final polish. You receive the completed video in all required formats for your intended uses.",
      duration: "1 week",
      deliverables: ["Final rendered video", "Multiple format exports", "Source files", "Usage guidelines"],
      durationIcon: "Clock",
      deliverablesIcon: "CheckCircle2",
      collaboration: {
        level: "High",
        icon: "✅",
        description: "Your final sign-off",
      },
      flexibility: "Final polish adjustments included",
    }
  ],
  
  timelineView: {
    quickOverview: {
      icon: "Clock",
      title: "Quick Timeline Overview",
      totalTime: "Total: 4-8 weeks",
      gates: "with 3 approval gates",
    },
    durationIcon: "Clock",
    involvementIcon: "Users",
    flexibilityIcon: "Shield",
    collaborationLevels: [
      {
        level: "Heavy",
        icon: "🤝",
        description: "Strategy sessions with your team",
      },
      {
        level: "High",
        icon: "🤝",
        description: "Your approval required",
      },
      {
        level: "Medium",
        icon: "👁️",
        description: "Review for technical accuracy",
      },
      {
        level: "Medium",
        icon: "👁️",
        description: "Preview checkpoints",
      },
      {
        level: "High",
        icon: "🤝",
        description: "Your feedback drives refinements",
      },
      {
        level: "High",
        icon: "✅",
        description: "Your final sign-off",
      },
    ],
    revisionPolicies: [
      "Unlimited strategy iterations",
      "Up to 2 revision rounds included",
      "Technical validation review",
      "Preview renders at 25%, 50%, 75%",
      "Up to 3 comprehensive revision rounds",
      "Final polish adjustments included",
    ],
    totalSummary: {
      icon: "CheckCircle",
      title: "Total Timeline: 4-8 Weeks",
      features: "3 Major Approval Gates • Pause/Resume Flexibility • Unlimited Communication",
      deliveryRate: "98%",
      deliveryText: "on-time delivery rate",
    },
  },
};

// ============================================
// CONTEXT
// ============================================

interface ProcessStepsContextType {
  data: ProcessStepsData;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ProcessStepsContext = createContext<ProcessStepsContextType | undefined>(undefined);

export function ProcessStepsProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<ProcessStepsData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const url = `${getApiUrl('process-page/steps')}?t=${timestamp}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch process steps data');
      }
      
      const result = await response.json();

      if (result.success && result.data) {
        // Normalize steps with proper deep merging of nested objects
        const normalizedSteps = (result.data.steps || DEFAULT_DATA.steps).map((step: any, index: number) => {
          const defaultStep = DEFAULT_DATA.steps[index];
          
          return {
            ...defaultStep,
            ...step,
            deliverables: Array.isArray(step.deliverables) 
              ? step.deliverables.map((d: any) => typeof d === 'string' ? d : (d?.text || d?.title || d?.name || 'New deliverable'))
              : defaultStep.deliverables,
            collaboration: {
              ...defaultStep.collaboration,
              ...(step.collaboration || {})
            },
            flexibility: step.flexibility || defaultStep.flexibility
          };
        });

        // Deep merge timelineView to handle nested objects
        const timelineView = {
          quickOverview: {
            ...DEFAULT_DATA.timelineView.quickOverview,
            ...(result.data.timelineView?.quickOverview || {})
          },
          durationIcon: result.data.timelineView?.durationIcon || DEFAULT_DATA.timelineView.durationIcon,
          involvementIcon: result.data.timelineView?.involvementIcon || DEFAULT_DATA.timelineView.involvementIcon,
          flexibilityIcon: result.data.timelineView?.flexibilityIcon || DEFAULT_DATA.timelineView.flexibilityIcon,
          collaborationLevels: result.data.timelineView?.collaborationLevels || DEFAULT_DATA.timelineView.collaborationLevels,
          revisionPolicies: result.data.timelineView?.revisionPolicies || DEFAULT_DATA.timelineView.revisionPolicies,
          totalSummary: {
            ...DEFAULT_DATA.timelineView.totalSummary,
            ...(result.data.timelineView?.totalSummary || {})
          },
        };

        setData({
          ...DEFAULT_DATA,
          ...result.data,
          steps: normalizedSteps,
          viewTabs: { ...DEFAULT_DATA.viewTabs, ...(result.data.viewTabs || {}) },
          centerSun: { ...DEFAULT_DATA.centerSun, ...(result.data.centerSun || {}) },
          timelineView: timelineView,
          stepCardExtras: result.data.stepCardExtras || undefined,
        });

      } else {
        logger.warn('⚠️ [ProcessSteps] API returned no data or success=false, using DEFAULT_DATA');
        // Use default data if API returns no data
        setData(DEFAULT_DATA);
      }
    } catch (err: any) {
      logger.error('❌ [ProcessSteps] Error fetching process steps data:', err);
      setError(err.message);
      // Use default data on error
      setData(DEFAULT_DATA);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <ProcessStepsContext.Provider value={{ data, loading, error, refetch: fetchData }}>
      {children}
    </ProcessStepsContext.Provider>
  );
}

export function useProcessSteps() {
  const context = useContext(ProcessStepsContext);
  if (context === undefined) {
    throw new Error('useProcessSteps must be used within a ProcessStepsProvider');
  }
  return context;
}